<template>
  <div class="home-page">
    <!-- Hero -->
    <section class="hero-section">
      <div class="hero-bg">
        <div class="hero-grid" />
        <div class="hero-glow glow-1" />
        <div class="hero-glow glow-2" />
      </div>
      <div class="hero-inner">
        <div class="hero-badge">
          <span class="badge-dot" /> {{ $t('view.home.badge') }}
        </div>
        <h1 class="hero-title">{{ $t('view.home.title') }}</h1>
        <p class="hero-desc">{{ $t('view.home.desc', { count: stats.toolCount }) }}</p>
        <div class="hero-search">
          <SearchBar @select="goTool" />
        </div>
        <div class="hero-stats">
          <div class="stat-item">
            <span class="stat-num">{{ stats.toolCount }}</span>
            <span class="stat-label">{{ $t('view.home.statTools') }}</span>
          </div>
          <div class="stat-divider" />
          <div class="stat-item">
            <span class="stat-num">{{ stats.catCount }}</span>
            <span class="stat-label">{{ $t('view.home.statCategories') }}</span>
          </div>
          <div class="stat-divider" />
          <div class="stat-item">
            <span class="stat-num">100%</span>
            <span class="stat-label">{{ $t('view.home.statFree') }}</span>
          </div>
        </div>
      </div>
    </section>

    <div class="page-body">
      <!-- 分类入口 -->
      <section class="section-block" v-if="categories.length">
        <div class="section-header">
          <h2 class="section-title">{{ $t('view.home.sectionCategoryTitle') }}</h2>
          <p class="section-sub">{{ $t('view.home.sectionCategorySub') }}</p>
        </div>
        <div class="category-grid">
          <router-link
            v-for="(cat, idx) in visibleCats"
            :key="cat.id"
            :to="`/category/${cat.slug}`"
            class="category-card"
          >
            <div class="cat-icon" :class="`cat-bg-${idx % 5}`">
              <i :class="catIcons[idx % catIcons.length]" />
            </div>
            <h3>{{ cat.name }}</h3>
            <p>{{ cat.toolCount || 0 }} {{ $t('view.common.toolCountUnit') }}</p>
          </router-link>
        </div>
        <div class="cat-toggle" v-if="categories.length > 5">
          <el-button size="small" plain @click="showAllCats=!showAllCats">
            {{ showAllCats ? $t('view.home.collapse') : $t('view.home.showAll', { count: categories.length }) }}
            <i :class="showAllCats?'el-icon-arrow-up':'el-icon-arrow-down'" />
          </el-button>
        </div>
      </section>

      <!-- 热门工具 -->
      <section class="section-block" v-if="hotTools.length">
        <div class="section-header">
          <h2 class="section-title">{{ $t('view.home.sectionHotTitle') }}</h2>
          <p class="section-sub">{{ $t('view.home.sectionHotSub') }}</p>
        </div>
        <div class="tool-grid">
          <template v-for="(item, idx) in mixedHotTools">
            <AdCard v-if="item._ad" :key="'a'+idx" :ad="item" />
            <ToolCard v-else :key="item.id" :tool="item" />
          </template>
        </div>
      </section>

      <!-- 最近更新 -->
      <section class="section-block" v-if="recentTools.length">
        <div class="section-header">
          <h2 class="section-title">{{ $t('view.home.sectionRecentTitle') }}</h2>
          <p class="section-sub">{{ $t('view.home.sectionRecentSub') }}</p>
        </div>
        <div class="tool-grid">
          <template v-for="(item, idx) in mixedRecentTools">
            <AdCard v-if="item._ad" :key="'b'+idx" :ad="item" />
            <ToolCard v-else :key="item.id" :tool="item" />
          </template>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import SearchBar from '@/components/SearchBar'
import ToolCard from '@/components/ToolCard'
import AdCard from '@/components/tool-components/AdCard'
import { getCategoryList, getHotTools, getRecentTools, getActiveAds } from '@/api/site'

function setMeta(name, content) {
  if (!content) return
  var el = document.querySelector('meta[name="' + name + '"]')
  if (!el) {
    el = document.createElement('meta'); el.name = name
    document.head.appendChild(el)
  }
  el.content = content
}

export default {
  components: { SearchBar, ToolCard, AdCard },
  created() {
    document.title = this.$t('view.home.docTitle')
    setMeta('description', this.$t('view.home.metaDescription'))
    setMeta('keywords', this.$t('view.home.metaKeywords'))
    this.loadData()
  },
  data() {
    return {
      categories: [],
      hotTools: [],
      recentTools: [],
      ads: [],
      showAllCats: false,
      stats: { toolCount: 0, catCount: 0 },
      catIcons: ['el-icon-document', 'el-icon-picture-outline', 'el-icon-tickets', 'el-icon-s-code', 'el-icon-lock', 'el-icon-s-tools', 'el-icon-connection', 'el-icon-s-data']
    }
  },
  computed: {
    visibleCats() {
      return this.showAllCats ? this.categories : this.categories.slice(0, 5)
    },
    mixedHotTools() {
      return this.mixAds(this.hotTools, 4)
    },
    mixedRecentTools() {
      return this.mixAds(this.recentTools, 4)
    }
  },
  methods: {
    loadData() {
      getCategoryList().then(res => {
        this.categories = res.data || []
        this.stats.catCount = this.categories.length
        this.stats.toolCount = this.categories.reduce((s,c) => s + (c.toolCount || 0), 0)
      })
      getHotTools(8).then(res => { this.hotTools = res.data || [] })
      getRecentTools(8).then(res => { this.recentTools = res.data || [] })
      getActiveAds('home').then(res => {
        this.ads = (res.data || []).map(a => { a._ad = true; return a })
      }).catch(() => { this.ads = [] })
    },
    mixAds(list, every) {
      if (!this.ads.length) return list
      const r = []; let ai = 0
      for (let i = 0; i < list.length; i++) {
        r.push(list[i])
        if ((i + 1) % every === 0 && ai < this.ads.length) {
          r.push(this.ads[ai++])
        }
      }
      return r
    },
    goTool(slug) { this.$router.push(`/tool/${slug}`) }
  }
}
</script>

<style scoped>
/* ========== Hero ========== */
.hero-section {
  position: relative;
  padding: 90px 20px 80px;
  text-align: center;
  color: #fff;
  overflow: hidden;
  background: linear-gradient(160deg, #0d1124 0%, #131a3a 30%, #182848 60%, #1a2350 100%);
}
.hero-bg { position: absolute; inset: 0; pointer-events: none; }

.hero-grid {
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(78,124,255,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(78,124,255,0.07) 1px, transparent 1px);
  background-size: 50px 50px;
  mask-image: radial-gradient(ellipse 70% 70% at 50% 50%, black 30%, transparent 70%);
}

.hero-glow {
  position: absolute; border-radius: 50%; filter: blur(100px);
}
.glow-1 { width: 500px; height: 500px; background: rgba(78,124,255,0.12); top: -150px; right: -100px; will-change: transform; }
.glow-2 { width: 400px; height: 400px; background: rgba(120,80,255,0.08); bottom: -120px; left: -80px; will-change: transform; }

.hero-particle {
  position: absolute; width: 3px; height: 3px; background: rgba(255,255,255,0.35);
  border-radius: 50%;
}

.hero-inner { max-width: 700px; margin: 0 auto; position: relative; z-index: 1; }

.hero-badge {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.18);
  border-radius: 20px; padding: 6px 18px; font-size: 13px;
  color: rgba(255,255,255,0.85); margin-bottom: 28px; letter-spacing: 2px;
}
.badge-dot { width: 6px; height: 6px; background: #4ef08e; border-radius: 50%; box-shadow: 0 0 8px #4ef08e; }

.hero-title {
  font-size: 52px; font-weight: 800; margin: 0 0 16px;
  letter-spacing: 6px;
  background: linear-gradient(180deg, #fff 0%, #bcc8e8 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text;
}
.hero-desc {
  font-size: 17px; margin: 0 0 36px;
  color: rgba(255,255,255,0.65); font-weight: 300; line-height: 1.6;
}
.hero-search { display: flex; justify-content: center; margin-bottom: 40px; }

.hero-stats { display: flex; align-items: center; justify-content: center; gap: 32px; }
.stat-item { text-align: center; }
.stat-num { font-size: 28px; font-weight: 700; color: #fff; display: block; }
.stat-label { font-size: 12px; color: rgba(255,255,255,0.5); margin-top: 2px; letter-spacing: 1px; }
.stat-divider { width: 1px; height: 32px; background: rgba(255,255,255,0.12); }

/* ========== Page Body ========== */
.page-body { max-width: 1200px; margin: 0 auto; padding: 48px 20px 80px; }

.section-block { margin-bottom: 56px; }
.section-header { margin-bottom: 28px; }
.section-title { font-size: 24px; font-weight: 700; color: #1a1f36; margin: 0; display: inline; }
.section-sub { font-size: 14px; color: #8892a6; margin: 6px 0 0; }

/* ========== Category Cards ========== */
.category-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; }
.category-card {
  background: #fff; border-radius: 16px; padding: 28px 20px 22px;
  text-align: center; transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
  border: 1px solid #ebeef5; display: flex; flex-direction: column; align-items: center;
  position: relative; overflow: hidden;
}
.category-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  opacity: 0; transition: opacity .3s;
}
.category-card:hover::before { opacity: 1; }
.category-card:hover {
  box-shadow: 0 8px 28px rgba(0,0,0,0.08);
  border-color: transparent;
  transform: translateY(-3px);
}
.cat-icon {
  width: 56px; height: 56px; border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 14px; position: relative;
}
.cat-icon::after {
  content: ''; position: absolute; inset: -4px; border-radius: 20px;
  opacity: 0.15; z-index: -1;
}
.cat-icon i { font-size: 26px; color: #fff; }
.cat-bg-0 { background: linear-gradient(135deg, #667eea, #764ba2); }
.cat-bg-0::after { background: #667eea; }
.cat-bg-1 { background: linear-gradient(135deg, #4facfe, #00f2fe); }
.cat-bg-1::after { background: #4facfe; }
.cat-bg-2 { background: linear-gradient(135deg, #43e97b, #38f9d7); }
.cat-bg-2::after { background: #43e97b; }
.cat-bg-3 { background: linear-gradient(135deg, #fa709a, #fee140); }
.cat-bg-3::after { background: #fa709a; }
.cat-bg-4 { background: linear-gradient(135deg, #a18cd1, #fbc2eb); }
.cat-bg-4::after { background: #a18cd1; }
.category-card h3 { margin: 0 0 4px; font-size: 15px; color: #1a1f36; font-weight: 700; }
.category-card p { margin: 0; font-size: 12px; color: #909399; }

.cat-toggle { text-align: center; margin-top: 16px; }

/* ========== Tool Grid ========== */
.tool-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }

/* ========== Responsive ========== */
@media (max-width: 1024px) { .category-grid { grid-template-columns: repeat(4, 1fr); } }
@media (max-width: 960px) {
  .category-grid { grid-template-columns: repeat(3, 1fr); }
  .tool-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .hero-title { font-size: 32px; letter-spacing: 4px; }
  .hero-section { padding: 60px 16px 50px; }
  .hero-desc { font-size: 15px; }
  .hero-stats { gap: 20px; }
  .category-grid { grid-template-columns: repeat(2, 1fr); }
  .tool-grid { grid-template-columns: 1fr; }
}
</style>
